// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// internal
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import ThreadsService from '../../services/threads.service';
import { palette } from '../../theme';
import { ThreadAudience, ThreadAudienceRole } from '../../models/thread.model';
import { Group } from '../../models/training.models';

export interface COENewThreadCardProps {
  open: boolean;
  onClose: () => void;
  refreshThreads: () => void;
  teamName?: string;
  groups?: Group[];
}

type RoleChipKey = ThreadAudienceRole;

const ROLE_CHIPS: Array<{ key: RoleChipKey; label: string; color: string; bg: string }> = [
  { key: 'coaches', label: 'Coaches', color: palette.success.dark, bg: palette.success.light },
  { key: 'guardians', label: 'Guardians', color: palette.warning.dark, bg: palette.warning.light },
];

const COECreateThreadCardComponent: FC<COENewThreadCardProps> = ({ open, onClose, refreshThreads, groups = [] }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [allTeam, setAllTeam] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState<ThreadAudienceRole[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Array<string | number>>([]);
  const [sending, setSending] = useState(false);

  const { showSnackbar } = useSnackbarHelper();
  const threadsService = useService(ThreadsService);
  const { id } = useParams();

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setAllTeam(true);
    setSelectedRoles([]);
    setSelectedGroupIds([]);
    setSending(false);
  }, [open]);

  const selectedGroupMembers = useMemo(() => {
    const members = new Map<string, { name: string; color: string; bg: string }>();
    selectedGroupIds.forEach((groupId) => {
      const group = groups.find((g) => String(g.id) === String(groupId));
      if (!group) return;
      (group.members || []).forEach((member: any) => {
        const name = member.name || `${member.firstname || ''} ${member.lastname || ''}`.trim();
        if (!name || members.has(name)) return;
        members.set(name, { name, color: palette.primary.main, bg: palette.primary.light });
      });
    });
    return Array.from(members.values());
  }, [selectedGroupIds, groups]);

  const buildAudience = (): ThreadAudience | undefined => {
    if (allTeam || (selectedRoles.length === 0 && selectedGroupIds.length === 0)) {
      return undefined;
    }
    const audience: ThreadAudience = {};
    if (selectedRoles.length > 0) audience.roles = selectedRoles;
    if (selectedGroupIds.length > 0) {
      audience.groupIds = selectedGroupIds.map((gid) => Number(gid));
    }
    return audience;
  };

  const handleSelectAllTeam = () => {
    setAllTeam(true);
    setSelectedRoles([]);
    setSelectedGroupIds([]);
  };

  const handleToggleRole = (role: ThreadAudienceRole) => {
    setAllTeam(false);
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleToggleGroup = (groupId: string | number) => {
    setAllTeam(false);
    setSelectedGroupIds((prev) =>
      prev.some((idVal) => String(idVal) === String(groupId))
        ? prev.filter((idVal) => String(idVal) !== String(groupId))
        : [...prev, groupId]
    );
  };

  const addNewThread = () => {
    if (!title.trim() || !description.trim()) {
      showSnackbar('Please add a title and description to your message', 'error');
      return;
    }

    const audience = buildAudience();
    const threadPayload: any = { title: title.trim(), message: description };
    if (audience) threadPayload.audience = audience;

    setSending(true);
    threadsService
      .createThread(threadPayload, id)
      .then(() => {
        refreshThreads();
        onClose();
      })
      .catch(() => {
        setSending(false);
        showSnackbar('There was an error posting this thread. Please try again later', 'error');
      });
  };

  if (!open) return null;

  const chipButtonStyle = (active: boolean, color: string, bg: string): React.CSSProperties => ({
    background: active ? bg : palette.grey[100],
    color: active ? color : palette.text.secondary,
    border: active ? `1px solid ${color}` : `1px solid ${palette.divider}`,
    padding: '5px 12px',
    borderRadius: 14,
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
  });

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        padding: '32px 40px',
        boxSizing: 'border-box',
        backgroundColor: palette.background.default,
      }}
    >
      <Typography variant="h5" style={{ fontWeight: 700, color: palette.text.primary, marginBottom: 20 }}>
        New Message
      </Typography>

      {/* To row */}
      <div style={{ borderBottom: `1px solid ${palette.grey[100]}`, paddingBottom: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
          <Typography
            style={{ fontSize: 13, fontWeight: 600, color: palette.text.secondary, width: 50, paddingTop: 4 }}
          >
            To
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1, minHeight: 28, alignItems: 'center' }}>
            {allTeam && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  background: palette.grey[100],
                  color: palette.text.secondary,
                }}
              >
                All Team
                <span
                  style={{ cursor: 'pointer', marginLeft: 2, fontSize: 13, opacity: 0.6 }}
                  onClick={() => setAllTeam(false)}
                >
                  ×
                </span>
              </span>
            )}
            {selectedRoles.map((role) => {
              const chip = ROLE_CHIPS.find((c) => c.key === role);
              if (!chip) return null;
              return (
                <span
                  key={role}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    background: chip.bg,
                    color: chip.color,
                  }}
                >
                  {chip.label}
                  <span
                    style={{ cursor: 'pointer', marginLeft: 2, fontSize: 13, opacity: 0.6 }}
                    onClick={() => handleToggleRole(role)}
                  >
                    ×
                  </span>
                </span>
              );
            })}
            {selectedGroupIds.map((groupId) => {
              const group = groups.find((g) => String(g.id) === String(groupId));
              return (
                <span
                  key={String(groupId)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    background: palette.primary.light,
                    color: palette.primary.main,
                  }}
                >
                  {group?.name ?? `Group ${groupId}`}
                  <span
                    style={{ cursor: 'pointer', marginLeft: 2, fontSize: 13, opacity: 0.6 }}
                    onClick={() => handleToggleGroup(groupId)}
                  >
                    ×
                  </span>
                </span>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginLeft: 60 }}>
          <button
            type="button"
            onClick={handleSelectAllTeam}
            style={chipButtonStyle(allTeam, palette.text.secondary, palette.grey[100])}
          >
            All Team
          </button>
          {ROLE_CHIPS.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => handleToggleRole(chip.key)}
              style={chipButtonStyle(selectedRoles.includes(chip.key), chip.color, chip.bg)}
            >
              {chip.label}
            </button>
          ))}
          {groups.map((group) => {
            const active = selectedGroupIds.some((gid) => String(gid) === String(group.id));
            return (
              <button
                key={String(group.id)}
                type="button"
                onClick={() => handleToggleGroup(group.id)}
                style={chipButtonStyle(active, palette.primary.main, palette.primary.light)}
              >
                {group.name}
              </button>
            );
          })}
        </div>

        {selectedGroupMembers.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginLeft: 60, marginTop: 8 }}>
            {selectedGroupMembers.map((member) => (
              <span
                key={member.name}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 8px',
                  borderRadius: 8,
                  fontSize: 10,
                  color: member.color,
                  background: member.bg,
                }}
              >
                {member.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Subject */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${palette.grey[100]}`,
          paddingBottom: 12,
          marginBottom: 14,
        }}
      >
        <Typography style={{ fontSize: 13, fontWeight: 600, color: palette.text.secondary, width: 50 }}>
          Subject
        </Typography>
        <TextField
          fullWidth
          variant="standard"
          placeholder="Enter subject..."
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: 14,
              color: palette.text.primary,
              padding: '6px 0',
            },
          }}
        />
      </div>

      {/* Body editor */}
      <div
        style={{
          flex: 1,
          minHeight: 220,
          border: `1px solid ${palette.divider}`,
          borderRadius: 12,
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        <ReactQuill
          theme="snow"
          value={description}
          onChange={setDescription}
          style={{ width: '100%', height: '100%' }}
          placeholder="Write your message..."
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        <Button
          onClick={addNewThread}
          color="primary"
          variant="contained"
          disabled={sending}
          sx={{ borderRadius: '10px', px: 3.5, py: 1, fontWeight: 600 }}
        >
          Send Message
        </Button>
        <Button variant="text" color="inherit" onClick={onClose} sx={{ color: palette.text.secondary }}>
          Discard
        </Button>
      </div>
    </Box>
  );
};

export default COECreateThreadCardComponent;
